<template>
    <div class="desktop-layout" :class="{ 'desktop-layout--side-pair': showSideSupportWidget }">
        <!-- 桌面端全局NavBar - 固定在顶部 -->
        <header class="desktop-navbar">
            <div class="navbar-container">
                <div class="navbar-left">
                    <div class="logo-section" @click="router.push('/')">
                        <img :src="logoUrl" alt="Logo" class="logo" />
                        <span class="brand-name">龙珠电竞</span>
                    </div>
                    <RouterLink class="desktop-home-link" :class="{ active: isHomeActive }" to="/">
                        &#22238;&#21040;&#39318;&#39029;
                    </RouterLink>
                    <button v-if="showGlobalBackButton" class="desktop-back-link" type="button" @click="handleBack">
                        <UndoOutlined />
                        <span>返回上一级</span>
                    </button>
                </div>

                <div class="navbar-right">
                    <div v-if="showNavbarSearch" class="navbar-search">
                        <a-input v-model:value="searchValue" placeholder="搜索游戏账号..." class="search-input"
                            allow-clear @change="handleSearchChange" @press-enter="handleSearch">
                            <template #suffix>
                                <SearchOutlined @click="handleSearch" />
                            </template>
                        </a-input>
                    </div>
                    <div v-if="isAuthenticated" class="user-section">
                        <a-dropdown>
                            <div class="user-info">
                                <a-avatar :src="userAvatar" :size="32">
                                    {{ userName?.charAt(0) || 'U' }}
                                </a-avatar>
                                <div class="user-details">
                                    <div class="user-name-row">
                                        <span class="user-name">{{ userName || '用户' }}</span>
                                        <span v-if="isMember" class="member-badge">会员</span>
                                    </div>
                                    <div class="user-balance">
                                        <span class="balance-indicator"></span>
                                        余额: {{ formatBalance(userBalance) }}
                                    </div>
                                </div>
                                <div class="user-actions">
                                    <div class="notifications-section">
                                        <div class="notification-bell" @click.stop="goNotifications">
                                            <a-badge :count="notificationsUnreadCount" :overflow-count="99">
                                                <BellOutlined />
                                            </a-badge>
                                        </div>
                                    </div>
                                    <div class="user-action-button" @click.stop="goProfile">
                                        <SettingOutlined />
                                    </div>
                                    <div v-if="canAccessAdmin" class="user-action-button" @click.stop="goAdmin">
                                        <CrownOutlined />
                                    </div>
                                    <div v-if="canAccessAgent" class="user-action-button" @click.stop="goAgent">
                                        <ShareAltOutlined />
                                    </div>
                                </div>
                                <DownOutlined />
                            </div>
                            <template #overlay>
                                <div class="user-dropdown-overlay">
                                    <div class="user-dropdown-row user-dropdown-row--primary">
                                        <button class="user-dropdown-item user-dropdown-item--primary" type="button"
                                            @click="router.push('/account/my')">
                                            <UserOutlined />
                                            <span>我的账号</span>
                                        </button>
                                        <button class="user-dropdown-item user-dropdown-item--primary" type="button"
                                            @click="goWallet">
                                            <WalletOutlined />
                                            <span>我的钱包</span>
                                        </button>
                                        <button class="user-dropdown-item user-dropdown-item--primary" type="button"
                                            @click="router.push('/order/list')">
                                            <FileTextOutlined />
                                            <span>我的订单</span>
                                        </button>
                                    </div>
                                    <div class="user-dropdown-row user-dropdown-row--secondary-single">
                                        <button class="user-dropdown-item user-dropdown-item--secondary" type="button"
                                            @click="handleLogout">
                                            <LogoutOutlined />
                                            <span>退出登录</span>
                                        </button>
                                    </div>
                                </div>
                            </template>
                        </a-dropdown>
                    </div>
                    <div v-else class="auth-buttons">
                        <a-button type="primary" @click="$router.push('/login')">登录</a-button>
                    </div>
                </div>
            </div>
        </header>

        <!-- 主内容区域 -->
        <main class="desktop-main-content" :class="contentClasses">
            <!-- PageHeader - 位于内容区域顶部的面包屑工具条 -->
            <header v-if="routePageHeader" class="page-header">
                <div class="page-header-content">
                    <div class="page-info">
                        <div class="page-title-section">
                            <!-- 返回按钮 -->
                            <a-button v-if="showBackButton" type="text" @click="handleBack" class="back-button">
                                <template #icon>
                                    <UndoOutlined />
                                </template>
                            </a-button>
                            <component v-if="routePageHeader.icon" :is="routePageHeader.icon" class="page-icon" />
                            <div class="page-text">
                                <h1 class="page-title">{{ routePageHeader.title }}</h1>
                                <p v-if="routePageHeader.description" class="page-description">{{
                                    routePageHeader.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-if="$slots['header-actions']" class="page-actions">
                        <slot name="header-actions" />
                    </div>
                </div>
            </header>

            <!-- 页面具体内容 -->
            <div class="page-content">
                <slot />
            </div>
        </main>

        <!-- 页面底部区域（可选） -->
        <footer v-if="$slots.footer" class="desktop-footer">
            <slot name="footer" />
        </footer>

        <AdminSupportFloatingWidget v-if="showAdminSupportWidget" />
        <UserSupportFloatingWidget v-else-if="showUserSupportWidget" />
        <NoticeFloatingButton :has-companion="showSideSupportWidget" />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import { useUserStore, useAuthStore, useWalletStore } from '@/stores'
import { useNotificationsStore } from '@/stores'
import { formatAmount } from '@/utils/formatters'
import { buildImageUrl } from '@/utils/imageHelpers'
import AdminSupportFloatingWidget from '@/components/support/AdminSupportFloatingWidget.vue'
import UserSupportFloatingWidget from '@/components/support/UserSupportFloatingWidget.vue'
import NoticeFloatingButton from '@/components/common/NoticeFloatingButton.vue'
import logoUrl from '@/assets/logo.jpg'
import {
    HomeOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    UserOutlined,
    FileTextOutlined,
    SettingOutlined,
    WalletOutlined,
    CrownOutlined,
    ShareAltOutlined,
    LogoutOutlined,
    DownOutlined,
    PlusOutlined,
    EditOutlined,
    CreditCardOutlined,
    SecurityScanOutlined,
    UndoOutlined,
    SwapOutlined,
    CustomerServiceOutlined
} from '@ant-design/icons-vue'
import { BellOutlined } from '@ant-design/icons-vue'

interface Props {
    // 样式控制
    containerClass?: string
    fullWidth?: boolean
    noPadding?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    containerClass: '',
    fullWidth: false,
    noPadding: false
})

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const authStore = useAuthStore()
const walletStore = useWalletStore()
const notificationsStore = useNotificationsStore()
const isHomeActive = computed(() => route.path === '/')
const showGlobalBackButton = computed(() => route.path !== '/')

// 图标映射表
const iconComponents = {
    HomeOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    UserOutlined,
    FileTextOutlined,
    SettingOutlined,
    WalletOutlined,
    CrownOutlined,
    PlusOutlined,
    EditOutlined,
    CreditCardOutlined,
    SecurityScanOutlined,
    UndoOutlined,
    SwapOutlined,
    BellOutlined,
    CustomerServiceOutlined
}

// 从路由 meta 读取 PageHeader 配置
const routePageHeader = computed(() => {
    const pageHeader = route.meta.pageHeader as {
        title: string
        description?: string
        icon?: string
    } | undefined
    if (!pageHeader) return null

    return {
        title: pageHeader.title,
        description: pageHeader.description,
        icon: pageHeader.icon ? iconComponents[pageHeader.icon as keyof typeof iconComponents] : null
    }
})

// 搜索功能
const getRouteSearchValue = () => {
    const raw = route.query.search ?? route.query.keyword
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' ? value.trim() : ''
}
const searchValue = ref(getRouteSearchValue())
const showNavbarSearch = computed(() => route.path !== '/market')

// 计算属性
const isAuthenticated = computed(() => authStore.isAuthenticated)
const userName = computed(() => userStore.currentUser?.nickname)
const isMember = computed(() => userStore.currentUser?.is_member === true)
const userAvatar = computed(() => {
    const avatar = userStore.currentUser?.avatar
    return avatar ? buildImageUrl(avatar) : ''
})
const userBalance = computed(() => {
    return walletStore.balance?.available_amount || 0
})

const canAccessAdmin = computed(() => {
    return authStore.canAccessAdmin
})

const canAccessAgent = computed(() => {
    return authStore.canAccessAgent
})

const showAdminSupportWidget = computed(() => {
    return authStore.canAccessAdmin && route.path !== '/admin/support/chat'
})

const showUserSupportWidget = computed(() => {
    return authStore.isAuthenticated && !authStore.canAccessAdmin
})

const showSideSupportWidget = computed(() => showAdminSupportWidget.value || showUserSupportWidget.value)

const contentClasses = computed(() => [
    props.containerClass,
    {
        'desktop-main-content--full-width': props.fullWidth || route.path === '/',
        'desktop-main-content--no-padding': props.noPadding || route.path === '/'
    }
])

// 判断是否显示返回按钮
const showBackButton = computed(() => {
    // 只在特定页面显示返回按钮
    return false
})

// 返回按钮处理
const handleBack = () => {
    // 特殊处理：订单详情页面返回到订单列表
    if (route.name === 'order-detail') {
        router.push('/order/list')
        return
    }

    if (window.history.state?.back) {
        router.back()
        return
    }

    router.push('/')
}

// 业务方法
const handleLogout = async () => {
    try {
        await authStore.logout()
        router.push('/login')
    } catch (error) {
        message.error((error as Error)?.message || '退出登录失败')
    }
}

const handleSearch = () => {
    const keyword = searchValue.value.trim()
    router.push({
        path: '/',
        hash: '#home-market-section',
        query: keyword ? { search: keyword } : {}
    })
}

const handleSearchChange = () => {
    if (!searchValue.value.trim() && getRouteSearchValue()) {
        handleSearch()
    }
}

const goProfile = () => {
    router.push('/profile/info')
}

const goNotifications = () => {
    router.push('/notifications')
}

const goAdmin = () => {
    router.push('/admin')
}

const goAgent = () => {
    router.push('/agent')
}

const goWallet = () => {
    router.push('/profile/wallet')
}

const formatBalance = (balance: number) => {
    return formatAmount(balance)
}

// ==================== 通知相关 ====================
const notificationsUnreadCount = computed(() => notificationsStore.unreadCount)

// ==================== 数据监听和初始化 ====================

// 监听认证状态变化
watch(() => authStore.isAuthenticated, async (isAuth) => {
    if (isAuth) {
        // 用户登录时加载余额（只在没有余额数据时加载）
        if (!walletStore.balance) {
            try {
                await walletStore.loadBalance()
            } catch (error) {
                console.error('加载余额失败:', error)
            }
        }
        // 初始化通知（轻量轮询未读数量 + 未读列表）
        try {
            await notificationsStore.initNotifications()
        } catch (error) {
            console.error('初始化通知失败:', error)
        }
    } else {
        // 用户登出时清除余额数据
        walletStore.balance = null
        notificationsStore.clearNotifications()
    }
})

// 组件挂载时的初始化
watch(() => [route.query.search, route.query.keyword], () => {
    const routeSearch = getRouteSearchValue()
    if (searchValue.value !== routeSearch) {
        searchValue.value = routeSearch
    }
})

onMounted(async () => {
    // 如果已经登录且没有余额数据，才加载余额信息
    if (authStore.isAuthenticated && !walletStore.balance) {
        try {
            await walletStore.loadBalance()
        } catch (error) {
            console.error('初始化加载余额失败:', error)
        }
    }
    // 已登录时初始化通知
    if (authStore.isAuthenticated) {
        try {
            await notificationsStore.initNotifications()
        } catch (error) {
            console.error('初始化通知失败:', error)
        }
    }
})
</script>

<style lang="less" scoped>
.desktop-layout {
    min-height: 100vh;
    background: var(--app-global-background);
    --page-header-surface: radial-gradient(760px 340px at 18% 0%, rgba(255, 255, 255, 0.08), transparent 62%),
        linear-gradient(180deg, rgba(31, 41, 55, 0.78), rgba(15, 23, 42, 0.68));
    --page-header-border: rgba(255, 255, 255, 0.12);
    --page-header-title: rgba(248, 250, 252, 0.96);
    --page-header-text: rgba(226, 232, 240, 0.92);
    --page-header-muted: rgba(148, 163, 184, 0.74);
    --page-header-btn-bg: rgba(2, 6, 23, 0.24);
    --page-header-btn-border: rgba(148, 163, 184, 0.22);
    --page-header-btn-hover-bg: rgba(255, 255, 255, 0.06);
    --page-header-btn-hover-border: rgba(226, 232, 240, 0.28);
    display: flex;
    flex-direction: column;
    /* 预留更多空间给固定在顶部的 Navbar，确保通知等内容不被遮挡 */
    padding-top: 84px;
}

.desktop-layout--side-pair :deep(.user-support-widget),
.desktop-layout--side-pair :deep(.admin-support-widget) {
    top: calc(50% - 32px);
}

// NavBar样式 - 半紫色玻璃效果，保证文字可读性
.desktop-navbar {
    background: rgba(2, 6, 23, 0.92);
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow:
        0 14px 44px rgba(0, 0, 0, 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1100;
    overflow: hidden;

    /* 玻璃效果的光泽 */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.28) 50%, transparent);
        z-index: 1;
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 1px;
        background: linear-gradient(90deg,
                transparent,
                rgba(148, 163, 184, 0.5) 25%,
                rgba(226, 232, 240, 0.35) 55%,
                rgba(56, 189, 248, 0.28) 85%,
                transparent);
        opacity: 0.9;
        z-index: 1;
    }

    .navbar-container {
        max-width: 1400px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 32px;
        height: 68px;
        position: relative;
        z-index: 2;
        /* 确保在光泽效果之上 */
    }

    .navbar-left {
        display: flex;
        align-items: center;
        gap: 14px;

        .logo-section {
            display: flex;
            align-items: center;
            gap: 10px;
            cursor: pointer;
            padding: 6px 10px;
            margin: -6px -10px;
            border-radius: 12px;
            transition: transform 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.08);
                box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
                transform: translate3d(0, -1px, 0);
            }

            .logo {
                height: 28px;
                width: auto;
            }

            .brand-name {
                font-size: 16px;
                font-weight: 600;
                color: #ffffff;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
            }
        }

        .desktop-home-link {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 34px;
            padding: 0 4px;
            color: #ffffff;
            font-size: 15px;
            font-weight: 750;
            line-height: 1;
            text-decoration: none;
            white-space: nowrap;
            transition: color 0.16s ease, text-shadow 0.16s ease, transform 0.16s ease;

            &:hover,
            &.active {
                color: #ffffff;
                text-shadow: 0 0 16px rgba(255, 255, 255, 0.36);
            }

            &:active {
                transform: translateY(1px);
            }
        }

        .desktop-back-link {
            height: 34px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 0 6px;
            border: 0;
            background: transparent;
            color: rgba(255, 255, 255, 0.92);
            font-size: 14px;
            font-weight: 760;
            line-height: 1;
            white-space: nowrap;
            cursor: pointer;
            transition: color 0.16s ease, text-shadow 0.16s ease, transform 0.16s ease;

            &:hover {
                color: #ffffff;
                text-shadow: 0 0 16px rgba(255, 255, 255, 0.34);
            }

            &:active {
                transform: translateY(1px);
            }

            :deep(.anticon) {
                font-size: 14px;
            }
        }
    }

    .navbar-right {
        display: flex;
        align-items: center;
        gap: 16px;

        .notifications-section {
            .notification-bell {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                border-radius: 18px;
                background: rgba(255, 255, 255, 0.09);
                border: 1px solid rgba(255, 255, 255, 0.14);
                cursor: pointer;
                transition: all 0.3s ease;

                &:hover {
                    background: rgba(255, 255, 255, 0.14);
                    transform: translateY(-2px);
                    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28);
                }

                :deep(.ant-badge) {
                    color: #ffffff;
                }

                :deep(.anticon) {
                    font-size: 18px;
                    color: #ffffff;
                }
            }
        }

        .navbar-search {
            .search-input {
                width: 300px;
                height: 36px;
                border-radius: 18px;
                background: rgba(255, 255, 255, 0.12);
                border: 1px solid rgba(255, 255, 255, 0.14);
                transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease, border-color 0.3s ease;

                &:hover {
                    background: rgba(255, 255, 255, 0.12);
                    border-color: rgba(255, 255, 255, 0.22);
                    transform: translateY(-1px);
                    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.22);
                }

                &:focus-within {
                    background: rgba(255, 255, 255, 0.14);
                    border-color: rgba(99, 102, 241, 0.5);
                    transform: translateY(-1px);
                    box-shadow:
                        0 14px 34px rgba(0, 0, 0, 0.28),
                        0 0 0 3px rgba(99, 102, 241, 0.18);
                }

                :deep(.ant-input) {
                    background: transparent;
                    border: none;
                    font-size: 14px;
                    color: #ffffff;
                    font-weight: 500;

                    &::placeholder {
                        color: rgba(255, 255, 255, 0.62);
                    }
                }

                :deep(.ant-input-suffix) {
                    color: rgba(255, 255, 255, 0.8);
                    cursor: pointer;
                    transition: all 0.2s ease;

                    &:hover {
                        color: #ffffff;
                        transform: scale(1.1);
                    }
                }
            }
        }

        .user-section {
            .user-info {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 8px 16px;
                border-radius: 12px;
                cursor: pointer;
                transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease, border-color 0.3s ease;
                background: rgba(255, 255, 255, 0.16);
                border: 1px solid rgba(255, 255, 255, 0.16);

                &:hover {
                    background: rgba(255, 255, 255, 0.2);
                    border-color: rgba(255, 255, 255, 0.24);
                    transform: translateY(-2px);
                    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
                }

                .user-details {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 2px;
                    min-width: 0;

                    .user-name-row {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        max-width: 150px;
                    }

                    .user-name {
                        font-size: 14px;
                        color: #ffffff;
                        font-weight: 500;
                        line-height: 1.2;
                        min-width: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .member-badge {
                        flex-shrink: 0;
                        height: 18px;
                        padding: 0 6px;
                        border-radius: 999px;
                        display: inline-flex;
                        align-items: center;
                        font-size: 11px;
                        font-weight: 800;
                        color: rgba(2, 6, 23, 0.92);
                        background: linear-gradient(135deg, #67e8f9, #2dd4bf);
                        box-shadow: 0 0 14px rgba(45, 212, 191, 0.35);
                    }

                    .user-balance {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-size: 12px;
                        color: rgba(255, 255, 255, 0.95);
                        font-weight: 500;
                        line-height: 1.2;
                        padding: 5px 10px;
                        background: rgba(255, 255, 255, 0.12);
                        border-radius: 14px;
                        border: 1px solid rgba(255, 255, 255, 0.14);

                        .balance-indicator {
                            width: 6px;
                            height: 6px;
                            border-radius: 50%;
                            background: #52c41a;
                            box-shadow: 0 0 6px rgba(82, 196, 26, 0.6);
                            flex-shrink: 0;
                        }
                    }
                }

                .user-actions {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                }

                .notifications-section {
                    .notification-bell {
                        width: 34px;
                        height: 34px;
                        border-radius: 12px;
                    }
                }

                .user-action-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    border-radius: 12px;
                    background: rgba(255, 255, 255, 0.09);
                    border: 1px solid rgba(255, 255, 255, 0.14);
                    cursor: pointer;
                    transition: all 0.3s ease;

                    &:hover {
                        background: rgba(255, 255, 255, 0.14);
                        transform: translateY(-2px);
                        box-shadow: 0 10px 26px rgba(0, 0, 0, 0.28);
                    }

                    :deep(.anticon) {
                        font-size: 18px;
                        color: #ffffff;
                    }
                }
            }
        }

        .auth-buttons {
            display: flex;
            gap: 8px;

            .ant-btn {
                border-radius: 10px;
                font-size: 14px;
                height: 36px;
                padding: 0 20px;
                font-weight: 500;
                transition: all 0.3s ease;

                &:not(.ant-btn-primary) {
                    background: rgba(255, 255, 255, 0.07);
                    color: #ffffff;
                    border: 1px solid rgba(255, 255, 255, 0.16);

                    &:hover {
                        background: rgba(255, 255, 255, 0.12);
                        border-color: rgba(255, 255, 255, 0.22);
                        transform: translateY(-2px);
                        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
                    }
                }

                &.ant-btn-primary {
                    background: linear-gradient(135deg, rgba(99, 102, 241, 0.92) 0%, rgba(168, 85, 247, 0.92) 100%);
                    border: 1px solid rgba(255, 255, 255, 0.16);
                    color: #ffffff;
                    font-weight: 600;

                    &:hover {
                        background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(168, 85, 247, 1) 100%);
                        transform: translateY(-2px);
                        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
                        color: #ffffff;
                    }
                }
            }
        }
    }
}

/* Dropdown 菜单 - 优雅的白色磨砂玻璃效果 */
:deep(.ant-dropdown-menu) {
    border-radius: 20px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.15),
        0 8px 24px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 1);
    position: relative;
    overflow: hidden;
    min-width: 200px;

    /* 顶部光泽线 */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg,
                transparent,
                rgba(255, 255, 255, 0.8) 50%,
                transparent);
        z-index: 1;
    }
}

:deep(.ant-dropdown-menu-item) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 14px;
    color: #2c2c2c;
    font-weight: 500;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    z-index: 2;
    margin: 2px 0;
    cursor: pointer;

    &:hover {
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
        color: #667eea;
        transform: translateY(-2px) scale(1.01);
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
    }

    &:active {
        transform: translateY(-1px) scale(1.005);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
    }
}

:deep(.ant-dropdown-menu-item .anticon) {
    font-size: 16px;
    color: #666666;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
    margin-right: 0;
    /* 去掉额外的 margin，使用父元素的 gap */
}

/* 悬停时图标颜色变化 */
:deep(.ant-dropdown-menu-item:hover .anticon) {
    color: #667eea;
    transform: scale(1.05);
}

:deep(.ant-dropdown-menu-item-divider) {
    margin: 12px 0;
    background: linear-gradient(90deg,
            transparent,
            rgba(102, 126, 234, 0.2) 20%,
            rgba(102, 126, 234, 0.15) 50%,
            rgba(118, 75, 162, 0.2) 80%,
            transparent);
    height: 1px;
    border: none;
}

:deep(.user-dropdown-overlay) {
    border-radius: 20px;
    padding: 12px;
    background:
        radial-gradient(520px 220px at 18% 20%, rgba(148, 163, 184, 0.14), transparent 62%),
        radial-gradient(520px 220px at 84% 18%, rgba(99, 102, 241, 0.12), transparent 62%),
        linear-gradient(180deg, rgba(2, 6, 23, 0.995), rgba(15, 23, 42, 0.99));
    border: 1px solid rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    box-shadow:
        0 22px 70px rgba(0, 0, 0, 0.45),
        0 10px 26px rgba(0, 0, 0, 0.28),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    min-width: 280px;
    display: grid;
    gap: 12px;
    overflow: hidden;
}

:deep(.user-dropdown-row) {
    display: grid;
    gap: 10px;
}

:deep(.user-dropdown-row--primary) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

:deep(.user-dropdown-row--secondary-single) {
    grid-template-columns: minmax(0, 1fr);
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

:deep(.user-dropdown-item) {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.16);
    color: rgba(255, 255, 255, 0.92);
    font-size: 13px;
    font-weight: 600;
    padding: 10px 12px;
    cursor: pointer;
    box-shadow:
        0 10px 22px rgba(0, 0, 0, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        background 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.user-dropdown-item--primary) {
    flex-direction: column;
    height: 78px;
    gap: 6px;
    padding: 12px 10px;
    font-size: 12px;
}

:deep(.user-dropdown-item--secondary) {
    height: 42px;
    justify-content: center;
    background: rgba(239, 68, 68, 0.22);
    border-color: rgba(239, 68, 68, 0.3);
    color: rgba(254, 226, 226, 0.92);
}

:deep(.user-dropdown-item:hover) {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.22) 0%, rgba(168, 85, 247, 0.18) 100%);
    border-color: rgba(99, 102, 241, 0.34);
    color: rgba(255, 255, 255, 0.98);
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.32);
}

:deep(.user-dropdown-item--secondary:hover) {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.32);
    color: rgba(255, 255, 255, 0.98);
}

:deep(.user-dropdown-item:active) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
}

:deep(.user-dropdown-item .anticon) {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.68);
    transition: color 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.user-dropdown-item--primary .anticon) {
    font-size: 18px;
}

:deep(.user-dropdown-item--secondary .anticon) {
    color: rgba(254, 202, 202, 0.9);
}

:deep(.user-dropdown-item:hover .anticon) {
    color: rgba(255, 255, 255, 0.92);
    transform: scale(1.06);
}

// PageHeader 样式 - 玻璃半透明效果，融入紫色主题
.page-header {
    background: var(--page-header-surface);
    border: 1px solid var(--page-header-border);
    border-radius: 16px;
    box-shadow:
        0 18px 56px rgba(0, 0, 0, 0.42),
        inset 0 1px 0 rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;

    /* 玻璃效果的光泽 */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg,
                transparent,
                rgba(255, 255, 255, 0.28) 50%,
                transparent);
    }

    .page-header-content {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 24px 28px;
        gap: 24px;
        position: relative;
        z-index: 1;

        .page-info {
            flex: 1;
            min-width: 0;

            .page-title-section {
                display: flex;
                align-items: flex-start;
                gap: 16px;

                .back-button {
                    color: var(--page-header-text);
                    border: 1px solid var(--page-header-btn-border);
                    background: var(--page-header-btn-bg);
                    border-radius: 8px;
                    height: 36px;
                    width: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-top: -2px;
                    transition: all 0.3s ease;

                    &:hover {
                        background: var(--page-header-btn-hover-bg);
                        border-color: var(--page-header-btn-hover-border);
                        color: rgba(255, 255, 255, 0.98);
                        transform: translateX(-2px);
                    }

                    :deep(.anticon) {
                        font-size: 16px;
                    }
                }

                .page-icon {
                    font-size: 28px;
                    color: var(--page-header-title);
                    filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.38));
                    flex-shrink: 0;
                    margin-top: 2px;
                    /* 与标题顶部对齐 */
                }

                .page-text {
                    flex: 1;
                    min-width: 0;

                    .page-title {
                        font-size: 24px;
                        font-weight: 600;
                        color: var(--page-header-title);
                        text-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
                        margin: 0 0 6px 0;
                        line-height: 1.3;
                    }

                    .page-description {
                        font-size: 14px;
                        color: var(--page-header-muted);
                        text-shadow: 0 10px 20px rgba(0, 0, 0, 0.28);
                        margin: 0;
                        line-height: 1.5;
                    }
                }
            }
        }

        .page-actions {
            display: flex;
            gap: 12px;
            align-items: flex-start;
            flex-shrink: 0;

            :deep(.ant-btn) {
                border-radius: 10px;
                height: 36px;
                padding: 0 16px;
                font-size: 14px;
                font-weight: 500;
                transition: all 0.3s ease;

                &:not(.ant-btn-primary) {
                    background: var(--page-header-btn-bg);
                    color: var(--page-header-text);
                    border: 1px solid var(--page-header-btn-border);
                    text-shadow: 0 10px 20px rgba(0, 0, 0, 0.28);

                    &:hover {
                        background: var(--page-header-btn-hover-bg);
                        border-color: var(--page-header-btn-hover-border);
                        transform: translateY(-2px);
                        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.32);
                    }
                }

                &.ant-btn-primary {
                    background: linear-gradient(135deg, rgba(56, 189, 248, 0.92) 0%, rgba(99, 102, 241, 0.92) 100%);
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    color: rgba(255, 255, 255, 0.96);
                    font-weight: 650;
                    text-shadow: none;

                    &:hover {
                        background: linear-gradient(135deg, rgba(56, 189, 248, 1) 0%, rgba(99, 102, 241, 1) 100%);
                        transform: translateY(-2px);
                        box-shadow: 0 20px 56px rgba(0, 0, 0, 0.42);
                        color: rgba(255, 255, 255, 0.98);
                    }
                }
            }
        }
    }
}

// 主内容样式
.desktop-main-content {
    flex: 1;
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px 32px;
    width: 100%;
    position: relative;
    z-index: 1;
    /* 确保在背景纹理之上 */

    .page-content {
        /* 页面具体内容区域 - 可以由子页面自定义样式 */
        min-height: 200px;
    }

    &.desktop-main-content--full-width {
        max-width: none;
        padding-left: 0;
        padding-right: 0;
    }

    &.desktop-main-content--no-padding {
        padding: 0;
    }

    @media (max-width: 1199px) {
        padding: 20px 24px;
    }

    @media (max-width: 1099px) {
        padding: 16px 20px;
    }
}

// 底部样式
.desktop-footer {
    padding: 24px;
    background: rgba(255, 255, 255, 0.8);
    border-top: 1px solid #e8e8e8;
    margin-top: auto;
}

// 桌面端响应式调整 - 专注1024px+的体验优化
@media (max-width: 1199px) {
    .desktop-navbar {
        .navbar-container {
            padding: 0 20px;
        }

        .navbar-right {
            .navbar-search {
                .search-input {
                    width: 240px; // 中等屏幕下缩小搜索框
                }
            }

            .user-section {
                .user-info {
                    .user-details {
                        .user-balance {
                            font-size: 11px; // 稍微缩小余额文字
                            padding: 4px 8px;
                        }
                    }
                }
            }
        }
    }

    .page-header {
        margin-bottom: 20px;
        border-radius: 14px;

        .page-header-content {
            padding: 20px 24px;

            .page-info {
                .page-title-section {
                    gap: 14px;

                    .page-icon {
                        font-size: 26px;
                    }

                    .page-text {
                        .page-title {
                            font-size: 22px;
                        }

                        .page-description {
                            font-size: 13px;
                        }
                    }
                }
            }

            .page-actions {
                :deep(.ant-btn) {
                    height: 34px;
                    font-size: 13px;
                    padding: 0 14px;
                }
            }
        }
    }
}

// 更小屏幕的桌面端优化
@media (max-width: 1099px) {
    .desktop-navbar {
        .navbar-right {
            .navbar-search {
                .search-input {
                    width: 200px; // 进一步缩小搜索框
                }
            }

            .user-section {
                .user-info {
                    padding: 6px 12px;
                    gap: 10px;

                    .user-details {
                        .user-name {
                            font-size: 13px;
                        }

                        .user-balance {
                            font-size: 10px;
                            padding: 3px 6px;
                        }
                    }
                }
            }

            .auth-buttons {
                .ant-btn {
                    height: 32px;
                    padding: 0 16px;
                    font-size: 13px;
                }
            }
        }
    }

    .page-header {
        .page-header-content {
            padding: 18px 20px;

            .page-info {
                .page-title-section {
                    gap: 12px;

                    .page-icon {
                        font-size: 24px;
                    }

                    .page-text {
                        .page-title {
                            font-size: 20px;
                        }
                    }
                }
            }
        }
    }
}
</style>
